import { useState, useEffect, useCallback, useMemo, useRef, memo } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { 
  Button,
  Input,
  Tooltip,
  Switch,
  Skeleton
} from "@heroui/react";
import {
  Archive,
  Building,
  Shield,
  Eye,
  PencilSimple,
  Plus,
  MagnifyingGlass,
  X,
  Warning,
  CheckCircle,
  Clock,
  User,
  CalendarBlank
} from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";
import { InsurancePolicy, InsurancePolicyStatus, PolicyMetrics } from "shared/types/insurance-policy";
// import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import InsuranceWarnings from "./InsuranceWarnings";
import AddPolicyModal from "./AddPolicyModal";
import BuildingSelector from "./BuildingSelector";

// Memoized table row component to prevent individual row re-renders
const PolicyTableRow = memo(({ 
  policy, 
  getStatusIcon, 
  isExpiringWithinThreeMonths,
  handleViewPolicy,
  handleEditPolicy,
  openBuildingsPicker,
  handleArchivePolicy 
}: { 
  policy: InsurancePolicy;
  getStatusIcon: (status: InsurancePolicyStatus) => JSX.Element;
  isExpiringWithinThreeMonths: (endDate?: Date) => boolean;
  handleViewPolicy: (policy: InsurancePolicy) => void;
  handleEditPolicy: (policy: InsurancePolicy) => void;
  openBuildingsPicker: (policy: InsurancePolicy) => void;
  handleArchivePolicy: (policyId: number) => void;
}) => {
  const getStatusChipColor = (status: InsurancePolicyStatus) => {
    switch (status) {
      case InsurancePolicyStatus.ACTIVE:
        return "bg-status-good/10 text-status-good border-status-good/30";
      case InsurancePolicyStatus.TERMINATING:
        return "bg-yellow-500/10 text-yellow-600 border-yellow-500/30";
      case InsurancePolicyStatus.TERMINATED:
        return "bg-red-500/10 text-red-600 border-red-500/30";
      default:
        return "bg-neutral-primary/10 text-neutral-primary border-neutral-primary/30";
    }
  };

  // Safe rendering with error boundaries
  try {
    return (
      <tr className="divide-x divide-neutral-primary/15 hover:bg-neutral-50/50 transition-colors">
        <td className="py-2.5 px-4">
          <div className="flex items-center gap-2">
            <Tooltip content="Bekijk details">
              <button className="text-moss-primary hover:text-moss-primary/80 transition-colors" onClick={() => handleViewPolicy(policy)}>
                <Eye weight="duotone" size={18} />
              </button>
            </Tooltip>
            <Tooltip content="Bewerk">
              <button 
                className="text-neutral-primary hover:text-neutral-primary/80 transition-colors"
                onClick={() => handleEditPolicy(policy)}
              >
                <PencilSimple weight="duotone" size={18} />
              </button>
            </Tooltip>
            <Tooltip content="Gebouwen beheren">
              <button 
                className="text-neutral-primary hover:text-moss-primary transition-colors"
                onClick={() => openBuildingsPicker(policy)}
              >
                <Building weight="duotone" size={18} />
              </button>
            </Tooltip>
            {!policy?.archived && (
              <Tooltip content="Archiveer">
                <button 
                  className="text-neutral-secondary-mid hover:text-red-500 transition-colors"
                  onClick={() => handleArchivePolicy(policy.id)}
                >
                  <Archive weight="duotone" size={18} />
                </button>
              </Tooltip>
            )}
          </div>
        </td>
        <td className="py-2.5 px-4">
          <span className="font-medium text-neutral-primary">{policy?.policyNumber || 'N/A'}</span>
        </td>
        <td className="py-2.5 px-4">
          <div>
            <p className="font-medium text-neutral-primary">{policy?.name || 'Unnamed Policy'}</p>
            {policy?.description && (
              <p className="text-sm text-neutral-secondary-mid mt-1">{policy.description}</p>
            )}
          </div>
        </td>
        <td className="py-2.5 px-4">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusChipColor(policy?.status || 'UNKNOWN')}`}>
              {getStatusIcon(policy?.status || 'UNKNOWN' as any)}
              {policy?.status || 'Unknown'}
            </span>
            {policy?.endDate && isExpiringWithinThreeMonths(policy.endDate) && policy.status === InsurancePolicyStatus.ACTIVE && (
              <Tooltip content="Verloopt binnen 3 maanden">
                <Warning className="h-4 w-4 text-yellow-500" />
              </Tooltip>
            )}
          </div>
        </td>
        <td className="py-2.5 px-4">
          {policy?.insuranceCompany?.name ? (
            <div className="flex items-center gap-1">
              <User className="h-4 w-4 text-neutral-secondary-mid" />
              <span className="text-sm text-neutral-primary">{policy.insuranceCompany.name}</span>
            </div>
          ) : (
            <span className="text-neutral-secondary-mid text-sm">N/A</span>
          )}
        </td>
        <td className="py-2.5 px-4">
          {policy?.broker?.name ? (
            <div className="flex items-center gap-1">
              <User className="h-4 w-4 text-neutral-secondary-mid" />
              <span className="text-sm text-neutral-primary">{policy.broker.name}</span>
            </div>
          ) : (
            <span className="text-neutral-secondary-mid text-sm">N/A</span>
          )}
        </td>
        <td className="py-2.5 px-4">
          {policy?.companies && policy.companies.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {policy.companies.slice(0, 2).map((company) => (
                <span key={company?.id || Math.random()} className="inline-block px-2 py-1 bg-neutral-100 text-neutral-primary rounded-sm text-xs font-medium">
                  {company?.name || 'Unknown'}
                </span>
              ))}
              {policy.companies && policy.companies.length > 2 && (
                <span className="inline-block px-2 py-1 bg-neutral-100 text-neutral-primary rounded-sm text-xs font-medium">
                  +{policy.companies.length - 2}
                </span>
              )}
            </div>
          ) : (
            <span className="text-neutral-secondary-mid text-sm">Geen</span>
          )}
        </td>
        <td className="py-2.5 px-4">
          {policy?.buildings && policy.buildings.length > 0 ? (
            <Tooltip 
              content={
                <div className="max-w-xs">
                  <div className="font-medium mb-2">Gedekte gebouwen ({policy.buildings.length}):</div>
                  <div className="grid grid-cols-2 gap-1">
                    {policy.buildings.map((building, index) => (
                      <div key={building?.id || index} className="text-xs py-1 px-2 bg-moss-primary/20 text-moss-primary rounded-sm">
                        {building?.id || 'Unknown'}
                      </div>
                    ))}
                  </div>
                </div>
              }
            >
              <div className="flex items-center gap-1 cursor-help">
                <Building className="h-4 w-4 text-moss-primary" />
                <span className="text-sm font-medium text-moss-primary">
                  {policy.buildings.length} gebouw{policy.buildings.length !== 1 ? 'en' : ''}
                </span>
                {policy.buildings.length > 0 && (
                  <div className="flex gap-0.5">
                    {policy.buildings.slice(0, 3).map((building, index) => (
                      <div key={building?.id || index} className="w-1.5 h-1.5 bg-moss-primary rounded-full"></div>
                    ))}
                    {policy.buildings.length > 3 && (
                      <div className="text-xs text-moss-primary/70 ml-1">+{policy.buildings.length - 3}</div>
                    )}
                  </div>
                )}
              </div>
            </Tooltip>
          ) : (
            <span className="text-neutral-secondary-mid text-sm">Geen</span>
          )}
        </td>
        <td className="py-2.5 px-4">
          {policy?.components && policy.components.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {policy.components.slice(0, 3).map((component, index) => (
                <span 
                  key={component?.id || index} 
                  className="inline-block px-2 py-1 bg-moss-primary/10 text-moss-primary rounded-sm text-xs font-medium border border-moss-primary/20"
                >
                  {component?.type || 'Unknown'}
                </span>
              ))}
              {policy.components && policy.components.length > 3 && (
                <span className="inline-block px-2 py-1 bg-moss-primary/10 text-moss-primary rounded-sm text-xs font-medium border border-moss-primary/20">
                  +{policy.components.length - 3}
                </span>
              )}
            </div>
          ) : (
            <span className="text-neutral-secondary-mid text-sm">Geen</span>
          )}
        </td>
        <td className="py-2.5 px-4">
          {policy?.coverageAmount ? (
            <span className="text-sm font-medium text-neutral-primary">
              €{policy.coverageAmount.toLocaleString('nl-NL')}
            </span>
          ) : (
            <span className="text-neutral-secondary-mid text-sm">N/A</span>
          )}
        </td>
        <td className="py-2.5 px-4">
          {policy?.yearlyPremium ? (
            <span className="text-sm font-medium text-neutral-primary">
              €{policy.yearlyPremium.toLocaleString('nl-NL')}
            </span>
          ) : (
            <span className="text-neutral-secondary-mid text-sm">N/A</span>
          )}
        </td>
        <td className="py-2.5 px-4">
          {policy?.endDate ? (
            <div className="flex items-center gap-1">
              <CalendarBlank className="h-4 w-4 text-neutral-secondary-mid" />
              <span className="text-sm text-neutral-primary">
                {new Date(policy.endDate).toLocaleDateString('nl-NL')}
              </span>
            </div>
          ) : (
            <span className="text-neutral-secondary-mid text-sm">N/A</span>
          )}
        </td>
        <td className="py-2.5 px-4">
          <span className="text-sm text-neutral-secondary-mid">
            {policy?.updatedAt ? new Date(policy.updatedAt).toLocaleDateString('nl-NL') : 'N/A'}
          </span>
        </td>
      </tr>
    );
  } catch (error) {
    console.error('Error rendering policy row:', error, policy);
    return (
      <tr className="divide-x divide-neutral-primary/15">
        <td colSpan={13} className="py-4 px-4 text-center text-red-500">
          Fout bij weergeven van polis (ID: {policy?.id || 'Unknown'})
        </td>
      </tr>
    );
  }
});

interface PolicyOverviewProps {
  hideMetrics?: boolean;
}

const PolicyOverview = ({ hideMetrics = false }: PolicyOverviewProps) => {
  const [policies, setPolicies] = useState<InsurancePolicy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [metrics, setMetrics] = useState<PolicyMetrics | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [viewModal, setViewModal] = useState<{ mode: 'view' | 'edit', policy: InsurancePolicy | null }>({ mode: 'view', policy: null });
  const { session } = useSession();
  // Buildings picker state
  const [buildingPickerFor, setBuildingPickerFor] = useState<InsurancePolicy | null>(null);

  // Filter policies based on search and archived status
  const filteredPolicies = useMemo(() => {
    let filtered = policies;

    // Filter by archived status
    if (!showArchived) {
      filtered = filtered.filter(policy => !policy.archived);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(policy =>
        policy.name.toLowerCase().includes(term) ||
        policy.policyNumber.toLowerCase().includes(term) ||
        policy.description?.toLowerCase().includes(term) ||
        policy.insuranceCompany?.name.toLowerCase().includes(term) ||
        policy.broker?.name.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [policies, showArchived, searchTerm]);

  // ⚡ Ultra-fast parallel data fetching - blazing performance!
  const fetchAllData = useCallback(async () => {
    if (!session) return;
    
    setIsLoading(true);
    
    try {
      const params = new URLSearchParams();
      if (showArchived) {
        params.append('includeArchived', 'true');
      }
      
      // 🚀 Fetch policies and metrics simultaneously for maximum speed
      const requests = [
        fetchWithAuth(`${API_URL}/insurance/policies?${params.toString()}`, { method: "GET" }, session)
      ];
      
      // Only fetch metrics if not hidden
      if (!hideMetrics) {
        requests.push(fetchWithAuth(`${API_URL}/insurance/policies/metrics`, { method: "GET" }, session));
      }
      
      const responses = await Promise.all(requests);
      const [policiesResponse, metricsResponse] = responses;
      
      // Process policies data
      if (policiesResponse.ok) {
        const policiesData = await policiesResponse.json();
        if (Array.isArray(policiesData)) {
          setPolicies(policiesData);
        } else {
          console.warn("Invalid policies data structure:", policiesData);
          setPolicies([]);
        }
      } else {
        setPolicies([]);
        console.error("Failed to fetch policies:", policiesResponse.status);
      }
      
      // Process metrics data only if not hidden
      if (!hideMetrics && metricsResponse?.ok) {
        const metricsData = await metricsResponse.json();
        if (metricsData && typeof metricsData === 'object') {
          setMetrics(metricsData);
        } else {
          console.warn("Invalid metrics data structure:", metricsData);
          setMetrics(null);
        }
      }
      
    } catch (error) {
      console.error("Failed to fetch policy data:", error);
      setPolicies([]);
      if (!hideMetrics) setMetrics(null);
    } finally {
      setIsLoading(false);
    }
  }, [session, showArchived, hideMetrics]);

  const initRef = useRef(false);
  useEffect(() => {
    if (!session) return;
    if (!initRef.current) {
      initRef.current = true;
      fetchAllData();
    }
  }, [session, fetchAllData]);

  // Stable effect for archived toggle - prevent unnecessary re-renders
  const showArchivedRef = useRef(showArchived);
  useEffect(() => {
    if (showArchivedRef.current !== showArchived) {
      showArchivedRef.current = showArchived;
      if (session) {
        fetchAllData();
      }
    }
  }, [showArchived, session, fetchAllData]);

  // Stable archive handler
  const handleArchivePolicy = useCallback(async (policyId: number) => {
    if (!session) return;
    
    const confirmed = window.confirm("Verzekeringspolis archiveren?");
    if (!confirmed) return;

    try {
      await fetchWithAuth(
        `${API_URL}/insurance/policies/${policyId}/archive`,
        { method: "PUT" },
        session
      );
      toast.success("Verzekeringspolis gearchiveerd!");
      fetchAllData();
    } catch (error) {
      toast.error("Fout bij archiveren van verzekeringspolis");
    }
  }, [session, fetchAllData]);

  const openBuildingsPicker = useCallback((policy: InsurancePolicy) => {
    setBuildingPickerFor(policy);
  }, []);

  const saveBuildingsForPolicy = useCallback(async (buildingIds: string[]) => {
    if (!session || !buildingPickerFor) return;
    
    // Calculate metrics change for soft update
    const currentBuildingIds = (buildingPickerFor.buildings || []).map(b => (b as any).id);
    const addedBuildings = buildingIds.filter(id => !currentBuildingIds.includes(id));
    const removedBuildings = currentBuildingIds.filter(id => !buildingIds.includes(id));
    const netChange = removedBuildings.length - addedBuildings.length; // Positive = more uncovered buildings
    
    try {
      // Soft update metrics immediately for better UX
      if (metrics && netChange !== 0) {
        setMetrics(prev => prev ? {
          ...prev,
          buildingsWithoutPolicies: Math.max(0, prev.buildingsWithoutPolicies + netChange)
        } : prev);
      }
      
      // Use the existing PUT /insurance/policies/:id endpoint which accepts buildingIds
      const res = await fetchWithAuth(
        `${API_URL}/insurance/policies/${buildingPickerFor.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            buildingIds: buildingIds // Send only buildingIds to update just the buildings
          })
        },
        session
      );
      
      if (!res.ok) {
        // Revert metrics update on error
        if (metrics && netChange !== 0) {
          setMetrics(prev => prev ? {
            ...prev,
            buildingsWithoutPolicies: Math.max(0, prev.buildingsWithoutPolicies - netChange)
          } : prev);
        }
        const errorData = await res.text();
        throw new Error(`HTTP ${res.status}: ${errorData}`);
      }
      
      // Get the updated policy from the response
      const updatedPolicy = await res.json();
      
      // Update the UI with the actual response from backend
      setPolicies(prev => prev.map(p => 
        p.id === buildingPickerFor.id ? updatedPolicy : p
      ));
      
      toast.success(`Gebouwen bijgewerkt: ${buildingIds.length} gebouw${buildingIds.length !== 1 ? 'en' : ''} geselecteerd`);
      // Keep modal open for multiple selections - don't close automatically
      // setBuildingPickerFor(null);
      
    } catch (error) {
      console.error("Error updating policy buildings:", error);
      toast.error("Fout bij opslaan van gebouwen");
    }
  }, [session, buildingPickerFor, metrics]);

  // Stable memoized helper functions
  const getStatusIcon = useCallback((status: InsurancePolicyStatus) => {
    switch (status) {
      case InsurancePolicyStatus.ACTIVE:
        return <CheckCircle className="h-4 w-4" />;
      case InsurancePolicyStatus.TERMINATING:
        return <Clock className="h-4 w-4" />;
      case InsurancePolicyStatus.TERMINATED:
        return <Warning className="h-4 w-4" />;
      default:
        return <Shield className="h-4 w-4" />;
    }
  }, []);

  const isExpiringWithinThreeMonths = useCallback((endDate?: Date) => {
    if (!endDate) return false;
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
    return new Date(endDate) <= threeMonthsFromNow;
  }, []);

  // Stable handlers for table actions
  const handleViewPolicy = useCallback((policy: InsurancePolicy) => {
    setViewModal({ mode: 'view', policy });
  }, []);

  const handleEditPolicy = useCallback((policy: InsurancePolicy) => {
    setViewModal({ mode: 'edit', policy });
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
  }, []);

  // Stable modal handlers - moved to top level
  const handleCloseAddModal = useCallback(() => setAddModalOpen(false), []);
  
  const handlePolicyCreated = useCallback((newPolicy: InsurancePolicy) => {
    setPolicies(prev => [newPolicy, ...prev]);
    if (!hideMetrics) fetchAllData();
  }, [hideMetrics, fetchAllData]);

  const handleCloseViewModal = useCallback(() => setViewModal({ mode: 'view', policy: null }), []);
  
  const handlePolicyUpdated = useCallback((updated: InsurancePolicy) => {
    setPolicies(prev => prev.map(p => p.id === updated.id ? updated : p));
    if (!hideMetrics) fetchAllData();
  }, [hideMetrics, fetchAllData]);

  const handleCloseBuildingSelector = useCallback(() => setBuildingPickerFor(null), []);

  // Memoized values for BuildingSelector
  const buildingSelectorTitle = useMemo(() => `Gebouwen beheren voor ${buildingPickerFor?.name || 'polis'}`, [buildingPickerFor?.name]);
  const buildingSelectorIds = useMemo(() => (buildingPickerFor?.buildings || []).map(b => (b as any).id), [buildingPickerFor]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-8 w-32" />
        </div>
        <div className="flex gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-32 rounded-lg" />
          ))}
        </div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield weight="duotone" size={24} className="text-moss-primary" />
          <h2 className="text-xl font-semibold text-neutral-primary">Verzekeringspolissen</h2>
        </div>
        <Button
          color="primary"
          startContent={<Plus className="h-4 w-4" />}
          className="bg-moss-primary text-white"
          onClick={() => setAddModalOpen(true)}
        >
          Nieuwe Polis
        </Button>
      </div>

      {/* Metrics - conditionally hidden */}
      {!hideMetrics && metrics && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="bg-white rounded-lg p-4 border border-neutral-primary/10 shadow-sm">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-moss-primary" />
              <span className="text-sm font-medium text-neutral-secondary-mid">Totaal</span>
            </div>
            <p className="text-2xl font-bold text-neutral-primary mt-1">{metrics.totalPolicies}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-neutral-primary/10 shadow-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-status-good" />
              <span className="text-sm font-medium text-neutral-secondary-mid">Actief</span>
            </div>
            <p className="text-2xl font-bold text-status-good mt-1">{metrics.activePolicies}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-neutral-primary/10 shadow-sm">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-500" />
              <span className="text-sm font-medium text-neutral-secondary-mid">Verlopend</span>
            </div>
            <p className="text-2xl font-bold text-yellow-500 mt-1">{metrics.expiringPolicies}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-neutral-primary/10 shadow-sm">
            <div className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-neutral-secondary-mid" />
              <span className="text-sm font-medium text-neutral-secondary-mid">Gearchiveerd</span>
            </div>
            <p className="text-2xl font-bold text-neutral-secondary-mid mt-1">{metrics.archivedPolicies}</p>
          </div>
          {metrics.buildingsWithoutPolicies > 0 && (
            <div className="bg-white rounded-lg p-4 border border-red-200 shadow-sm">
              <div className="flex items-center gap-2">
                <Warning className="h-5 w-5 text-red-500" />
                <span className="text-sm font-medium text-red-600">Ongedekt</span>
              </div>
              <p className="text-2xl font-bold text-red-500 mt-1">{metrics.buildingsWithoutPolicies}</p>
              <p className="text-xs text-red-600 mt-1">gebouwen</p>
            </div>
          )}
          {metrics.policiesWithoutPayments > 0 && (
            <div className="bg-white rounded-lg p-4 border border-orange-200 shadow-sm">
              <div className="flex items-center gap-2">
                <Warning className="h-5 w-5 text-orange-500" />
                <span className="text-sm font-medium text-orange-600">Geen betaling</span>
              </div>
              <p className="text-2xl font-bold text-orange-500 mt-1">{metrics.policiesWithoutPayments}</p>
              <p className="text-xs text-orange-600 mt-1">dit jaar</p>
            </div>
          )}
        </div>
      )}

      {/* Warnings */}
      <InsuranceWarnings />

      {/* Filters */}
      <div className="bg-white rounded-lg p-4 border border-neutral-primary/10 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <Input
            placeholder="Zoek op naam, polisnummer, verzekeraar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            startContent={<MagnifyingGlass className="h-4 w-4 text-neutral-secondary-mid" />}
            endContent={
              searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="text-neutral-secondary-mid hover:text-neutral-primary transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )
            }
            classNames={{
              base: "w-72",
              input: "text-sm",
              inputWrapper: "bg-white border border-neutral-primary/20 data-[hover=true]:border-moss-primary/40 group-data-[focus=true]:border-moss-primary"
            }}
          />
          <Switch
            isSelected={showArchived}
            onValueChange={setShowArchived}
            size="sm"
            color="warning"
            startContent={<Archive className="h-4 w-4" />}
            classNames={{
              base: "inline-flex flex-row-reverse w-auto max-w-md bg-white border border-neutral-primary/20 rounded-lg px-2 py-1",
              wrapper: "p-0 h-4 overflow-visible",
              thumb: "w-6 h-6 border-2 shadow-lg",
              label: "text-sm text-neutral-primary font-medium mr-2"
            }}
          >
            Toon gearchiveerd
          </Switch>
        </div>
      </div>

      {/* Policies Table */}
      <div className="flex flex-col divide-y divide-neutral-primary/10 rounded-lg bg-white shadow-sm">
        <div className="w-full overflow-scroll">
          <table className="w-full">
            <thead className="h-fit border-b-2 border-neutral-primary/40">
              <tr className="divide-x divide-neutral-primary/15">
                <th className="bg-white py-2 px-4 h-fit text-sm text-neutral-primary font-medium text-left">Acties</th>
                <th className="bg-white py-2 px-4 h-fit text-sm text-neutral-primary font-medium text-left">Polisnummer</th>
                <th className="bg-white py-2 px-4 h-fit text-sm text-neutral-primary font-medium text-left">Naam</th>
                <th className="bg-white py-2 px-4 h-fit text-sm text-neutral-primary font-medium text-left">Status</th>
                <th className="bg-white py-2 px-4 h-fit text-sm text-neutral-primary font-medium text-left">Verzekeraar</th>
                <th className="bg-white py-2 px-4 h-fit text-sm text-neutral-primary font-medium text-left">Broker</th>
                <th className="bg-white py-2 px-4 h-fit text-sm text-neutral-primary font-medium text-left">Bedrijven</th>
                <th className="bg-white py-2 px-4 h-fit text-sm text-neutral-primary font-medium text-left">Gebouwen</th>
                <th className="bg-white py-2 px-4 h-fit text-sm text-neutral-primary font-medium text-left">Componenten</th>
                <th className="bg-white py-2 px-4 h-fit text-sm text-neutral-primary font-medium text-left">Dekking</th>
                <th className="bg-white py-2 px-4 h-fit text-sm text-neutral-primary font-medium text-left">Premie/jaar</th>
                <th className="bg-white py-2 px-4 h-fit text-sm text-neutral-primary font-medium text-left">Vervaldatum</th>
                <th className="bg-white py-2 px-4 h-fit text-sm text-neutral-primary font-medium text-left">Laatst bijgewerkt</th>
              </tr>
            </thead>
            <tbody className="border-b divide-y divide-neutral-primary/15">
              {filteredPolicies.length === 0 ? (
                <tr>
                  <td colSpan={13} className="py-16 px-8">
                    <div className="flex flex-col items-center gap-2 text-center">
                      <Shield className="h-12 w-12 text-neutral-secondary-mid" />
                      <p className="text-neutral-secondary-mid">Geen verzekeringspolicies gevonden</p>
                      {searchTerm && (
                        <button
                          onClick={handleClearSearch}
                          className="mt-2 text-moss-primary hover:text-moss-primary/80 font-medium transition-colors text-sm"
                        >
                          Wis zoekopdracht
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPolicies.map((policy: InsurancePolicy) => (
                  <PolicyTableRow
                    key={policy.id}
                    policy={policy}
                    getStatusIcon={getStatusIcon}
                    isExpiringWithinThreeMonths={isExpiringWithinThreeMonths}
                    handleViewPolicy={handleViewPolicy}
                    handleEditPolicy={handleEditPolicy}
                    openBuildingsPicker={openBuildingsPicker}
                    handleArchivePolicy={handleArchivePolicy}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Policy Modal */}
      <AddPolicyModal
        isOpen={addModalOpen}
        onClose={handleCloseAddModal}
        onCreated={handlePolicyCreated}
      />

      {/* Reused Modal for view/edit */}
      <AddPolicyModal
        isOpen={!!viewModal.policy}
        onClose={handleCloseViewModal}
        mode={viewModal.mode}
        policy={viewModal.policy}
        onUpdated={handlePolicyUpdated}
      />

      {/* Building Selector Modal */}
      <BuildingSelector
        isOpen={!!buildingPickerFor}
        onClose={handleCloseBuildingSelector}
        selectedBuildingIds={buildingSelectorIds}
        onSave={saveBuildingsForPolicy}
        onRealTimeUpdate={saveBuildingsForPolicy} // Enable real-time saving
        title={buildingSelectorTitle}
        description="Zoek en vink meerdere gebouwen aan. We tonen de referentiecodes (bijv. 2000-EMIE-12)."
      />
    </div>
  );
};

export default memo(PolicyOverview);
